import React from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';
import { Paper } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: theme.palette.grey[100],
        },

        // ── Header ──
        templateHeader: {
            backgroundColor: '#fff',
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1.25, 2),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.25),
            flexShrink: 0,
        },
        backButton: {
            color: theme.palette.text.secondary,
            padding: 6,
            // Same small nudge-left on hover as the Enquiry form's header back link (headerBackLink in enquiryFormDrawerStyles.ts).
            '& .MuiSvgIcon-root': {
                transition: 'transform 0.15s ease',
            },
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
                color: theme.palette.text.primary,
                '& .MuiSvgIcon-root': {
                    transform: 'translateX(-2px)',
                },
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover .MuiSvgIcon-root': {
                    transform: 'none',
                },
            },
        },
        headerDivider: {
            width: 1,
            height: 28,
            backgroundColor: theme.palette.divider,
            marginRight: theme.spacing(0.25),
            flexShrink: 0,
        },
        templateIcon: {
            width: 36,
            height: 36,
            borderRadius: 8,
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
            flexShrink: 0,
        },
        headerText: {
            flex: 1,
            minWidth: 0,
        },
        templateTitle: {
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        templateSubtitle: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },

        // ── Body ──
        body: {
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
        },

        // ── Main ──
        main: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: theme.palette.grey[50],
        },
        contentControl: {
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 2px 4px rgba(0,0,0,0.07)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            padding: theme.spacing(1, 1.5),
        },
        contentControlIconWrap: {
            width: 32,
            height: 32,
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        contentControlText: {
            minWidth: 0,
            flex: 1,
        },
        contentControlTitle: {
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        contentControlSubtitle: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        contentControlIconBtn: {
            padding: 4,
            borderRadius: 6,
            color: '#70d389',
            '&:hover': {
                backgroundColor: theme.palette.grey[100],
            },
        },
        contentControlAction: {
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        mainContent: {
            flex: 1,
            overflow: 'auto',
            padding: '20px 20px',
        },
        // Same rounded-card framing as rv-grid-container (used for Task/Enquiry grids), but
        // without its overflow:hidden/max-height:100% — tab content here is form-based and
        // needs to grow taller than the viewport and scroll via mainContent's own overflow,
        // not be clipped like a self-scrolling ag-grid would be.
        contentCard: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 8,
            boxShadow: '0 1px 6px rgba(0,0,0,0.14)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            padding: '15px',
            minHeight: '100%',
        },
    })
);

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ContentControl {
    title?: string;
    subtitle?: string;
    icon?: React.ReactNode;
    iconBg?: string;
    iconColor?: string;
    actions?: {
        onCreate?: () => void;
        onEdit?: () => void;
        onDelete?: () => void;
        onSave?: () => void;
    };
    loading?: boolean;
}

export interface ContentLayoutProps {
    children?: React.ReactNode;
    // ── Header ──
    /** Icon shown in the header avatar */
    icon?: React.ReactElement;
    /** Main page title — omit to hide the header entirely */
    title?: string;
    /** Optional subtitle */
    subtitle?: string;
    /** If provided, renders a back arrow */
    onBack?: () => void;
    /** Tooltip for the back button (default: "Back") */
    backTooltip?: string;

    contentControl?: ContentControl;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ContentLayout: React.FC<
    ContentLayoutProps
> = ({
    icon,
    title,
    subtitle,
    onBack,
    backTooltip = 'Back',
    children,
    contentControl,
}) => {
        const classes = useStyles();

        const hasContentControl = Boolean(contentControl?.title || contentControl?.subtitle || contentControl?.actions);

        return (
            <div className={classes.root}>

                {/* ── Header — hidden when no title is provided ── */}
                {title && (
                    <div className={classes.templateHeader}>
                        {onBack && (
                            <>
                                <Tooltip title={backTooltip} placement="bottom">
                                    <IconButton
                                        className={classes.backButton}
                                        size="small"
                                        onClick={onBack}
                                        aria-label={backTooltip}
                                    >
                                        <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <div className={classes.headerDivider} />
                            </>
                        )}
                        {icon && (
                            <Avatar className={classes.templateIcon} variant="rounded">
                                {icon}
                            </Avatar>
                        )}
                        <div className={classes.headerText}>
                            <Typography className={classes.templateTitle}>{title}</Typography>
                            {subtitle && (
                                <Typography className={classes.templateSubtitle}>{subtitle}</Typography>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Body ── */}
                <div className={classes.body}>

                    {/* ── Main content ── */}
                    <div className={classes.main}>
                        {hasContentControl && (
                            <Paper square elevation={0} className={classes.contentControl}>
                                {contentControl?.icon && !contentControl?.loading && (
                                    <div
                                        className={classes.contentControlIconWrap}
                                        style={{
                                            backgroundColor: contentControl.iconBg ?? '#e3f2fd',
                                            color: contentControl.iconColor ?? '#1565c0',
                                        }}
                                    >
                                        {contentControl.icon}
                                    </div>
                                )}
                                <div className={classes.contentControlText}>
                                    {contentControl?.loading ? (
                                        <>
                                            <SkeletonLoader
                                                variant="text"
                                                rows={1}
                                                widths={['42%']}
                                                heights={[14]}
                                            />
                                            <SkeletonLoader
                                                variant="text"
                                                rows={1}
                                                widths={['28%']}
                                                heights={[11]}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            {contentControl?.title && (
                                                <Typography className={classes.contentControlTitle}>
                                                    {contentControl.title}
                                                </Typography>
                                            )}
                                            {contentControl?.subtitle && (
                                                <Typography className={classes.contentControlSubtitle}>
                                                    {contentControl.subtitle}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                </div>
                                {contentControl?.actions && (
                                    <div className={classes.contentControlAction}>
                                        {contentControl.actions.onSave && (
                                            <Tooltip title={'Save'}>
                                                <IconButton
                                                    size="medium"
                                                    className={classes.contentControlIconBtn}
                                                    onClick={contentControl.actions.onSave}
                                                    aria-label={'Save'}
                                                    disabled={contentControl.loading}
                                                >
                                                    <SaveIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {contentControl.actions.onCreate && (
                                            <Tooltip title={'Create new'}>
                                                <IconButton
                                                    size="medium"
                                                    className={classes.contentControlIconBtn}
                                                    onClick={contentControl.actions.onCreate}
                                                    aria-label={'Create new'}
                                                    disabled={contentControl.loading}
                                                >
                                                    <AddIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {contentControl.actions.onEdit && (
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="medium"
                                                    className={classes.contentControlIconBtn}
                                                    onClick={contentControl.actions.onEdit}
                                                    aria-label={'Edit'}
                                                    disabled={contentControl.loading}
                                                >
                                                    <EditIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {contentControl.actions.onDelete && (
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="medium"
                                                    className={classes.contentControlIconBtn}
                                                    onClick={contentControl.actions.onDelete}
                                                    aria-label={'Delete'}
                                                    disabled={contentControl.loading}
                                                >
                                                    <DeleteIcon fontSize="medium" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                            </Paper>
                        )}
                        <div className={classes.mainContent}>
                            <div className={classes.contentCard}>
                                {children}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        );
    };